import Debug from 'debug';

import { BrowserAuthError } from '@azure/msal-browser';
import { RestError } from '../errors';
import { joinURLPath } from '../lib/url';
import { wait } from '../lib/wait';
import { getMsalInstance } from '../services/msalInstance';
import { getTokenRequest } from '../services/msalcommon';
import { isApiSupported } from './office';

const debug = Debug('o365:OutlookRestApi');

export enum RestEndpoint {
  GetItem,
  SendEmail,
  UpdateItem,
  MailFolders,
}

export enum OutlookRestBodyType {
  Text = 'Text',
  HTML = 'HTML',
}

export type OutlookRestMessage = {
  Body: {
    ContentType: OutlookRestBodyType;
    Content: string;
  };
  ChangeKey: string;
  ParentFolderId: string;
};

type DialogEventMessage = { message: string };
type DialogEventError = { error: number };
type DialogEventArg = DialogEventMessage | DialogEventError;

/** Gets a bearer token usable for calling Graph REST endpoints. */
export async function getGraphAccessToken(): Promise<string> {
  const scopes = ['Mail.Send', 'Mail.ReadWrite', 'Files.Read', 'User.Read', 'openid', 'profile'];
  const msalInstance = await getMsalInstance();
  let accessToken: string | undefined;

  // Attempt to aquire the token silently first.
  try {
    const userAccount = await msalInstance.acquireTokenSilent(getTokenRequest(scopes, false));
    debug('Acquired token silently:');
    accessToken = userAccount.accessToken;
  } catch (error) {
    debug(`Unable to acquire token silently: ${error}`);
  }

  // If we don't have an access token, try to acquire it interactively.
  // This will open a popup for the user to log in and accept requested permissions.
  let attemptDialogLogin = false;
  if (!accessToken) {
    try {
      debug('Attempting to acquire token interactively...');
      const userAccount = await msalInstance.acquireTokenPopup(getTokenRequest(scopes, true));
      debug('Acquired token interactively.');
      accessToken = userAccount.accessToken;
    } catch (popupError) {
      // Optional fallback if about:blank popup should not be shown
      if (popupError instanceof BrowserAuthError && popupError.errorCode === 'popup_window_error') {
        attemptDialogLogin = true;
      } else {
        debug(`Unable to acquire token interactively: ${popupError}`);
        throw popupError;
      }
    }
  }

  // Attempt dialog login if NAA is not supported or if the interactive login failed.
  if (attemptDialogLogin) {
    debug('Attempting to acquire token using the office dialog API...');
    try {
      accessToken = await getTokenWithDialogApi();
    } catch (error) {
      debug(`Unable to acquire token using dialog API: ${error}`);
      throw error;
    }
  }

  if (!accessToken) {
    debug('No access token could be acquired.');
    throw new Error('No access token acquired.');
  }

  return accessToken;
}

async function getTokenWithDialogApi(): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      Office.context.ui.displayDialogAsync(
        `https://${window.location.host}/fallback`,
        { height: 60, width: 30 },
        (result) => {
          result.value.addEventHandler(Office.EventType.DialogEventReceived, (arg: DialogEventArg) => {
            const errorArg = arg as DialogEventError;
            if (errorArg.error === 12006) {
              reject('Dialog closed');
            }
          });

          result.value.addEventHandler(Office.EventType.DialogMessageReceived, (arg: DialogEventArg) => {
            const messageArg = arg as DialogEventMessage;
            const parsedMessage = JSON.parse(messageArg.message);
            result.value.close();

            if (parsedMessage.error) {
              reject(parsedMessage.error);
            } else {
              resolve(parsedMessage.accessToken);
            }
          });
        },
      );
    } catch (error) {
      debug('Error opening dialog', error);
      reject(error);
    }
  });
}

/**
 * Creates an initial set of fetch parameters, including adding the authorization header.
 * @arg token The token to use as the bearer token.
 * @arg extraHeaders Any extra headers to apply after the default headers.
 * @returns an object ready for additional parameters before sending to fetch.
 */
export function getOutlookDefaultFetchParams(
  token: string,
  extraHeaders: Record<string, string> = {},
): RequestInit {
  return {
    mode: 'cors',
    headers: {
      accept: 'application/json',
      authorization: `Bearer ${token}`,
      'content-type': 'application/json',
      ...extraHeaders,
    },
  };
}

/**
 * Gets the change key for the given Outlook item. Can be used to detect server-side updates by
 * comparing a previous change key to the current change key.
 * @arg p The required parameters for the function.
 * @arg p.itemId The item ID from which to grab the change key.
 * @arg p.token The Outlook API token.
 * @arg signal The abort signal to pass to the fetch function to control how long the call takes.
 * @returns a string containing the change key or null if none was found.
 */
export async function getOutlookItemChangeKey(
  { itemId, token }: { itemId: string; token: string },
  signal?: AbortSignal,
): Promise<string | null> {
  const { method, url } = getOutlookURL(itemId, RestEndpoint.GetItem);

  url.searchParams.append('$select', 'ChangeKey');

  const params: RequestInit = {
    ...getOutlookDefaultFetchParams(token),
    method,
    cache: 'no-cache',
    signal,
  };

  const response = await fetch(url.toString(), params);
  if (response.ok) {
    const message = (await response.json()) as Partial<OutlookRestMessage>;
    return message?.ChangeKey || null;
  }

  return null;
}

/**
 * Gets the parent folder id for the given Outlook item.
 * @arg p The required parameters for the function.
 * @arg p.itemId The item ID from which to grab the parent folder.
 * @arg p.token The Outlook API token.
 * @arg signal The abort signal to pass to the fetch function to control how long the call takes.
 * @returns a string containing the parent folder or null if none was found.
 */
export async function getOutlookItemParentFolder(
  { itemId, token }: { itemId: string; token: string },
  signal?: AbortSignal,
): Promise<string | null> {
  const { method, url } = getOutlookURL(itemId, RestEndpoint.GetItem);

  url.searchParams.append('$select', 'ParentFolderId');

  const params: RequestInit = {
    ...getOutlookDefaultFetchParams(token),
    method,
    cache: 'no-cache',
    signal,
  };

  const response = await fetch(url.toString(), params);
  if (response.ok) {
    const message = (await response.json()) as Partial<OutlookRestMessage>;
    return message?.ParentFolderId || null;
  }

  return null;
}

/**
 * Converts the given parameters in to a usable Outlook REST API URL.
 * @arg itemId The unique item id that the URL should include.
 * @arg type The well-known REST endpoint to build a URL for.
 * @returns A URL that can be used to call against the Outlook REST API.
 */
export function getOutlookURL(
  itemId: string | null,
  type: RestEndpoint,
): { url: URL; method: 'GET' | 'POST' | 'PATCH' | 'DELETE' } {
  const url = new URL('https://graph.microsoft.com');

  switch (type) {
    case RestEndpoint.GetItem:
      return {
        method: 'GET',
        url: new URL(joinURLPath(url, `/v1.0/me/messages/${itemId}`), url),
      };
    case RestEndpoint.SendEmail:
      return {
        method: 'POST',
        url: new URL(joinURLPath(url, `/v1.0/me/messages/${itemId}/send`), url),
      };
    case RestEndpoint.UpdateItem:
      return {
        method: 'PATCH',
        url: new URL(joinURLPath(url, `/v1.0/me/messages/${itemId}`), url),
      };
    case RestEndpoint.MailFolders:
      return {
        method: 'GET',
        url: new URL(joinURLPath(url, '/v1.0/me/mailFolders/'), url),
      };
    default:
      throw new Error(`Unrecognized Graph rest type: '${type}'`);
  }
}

/**
 * Monitors (polls) the Outlook server waiting for an item to be updated before returning.
 * @arg p The required parameters for the function.
 * @arg p.bodyId The searchable token to be found in the body of the email, indicating the
 *      body has been updated. Used only for detecting changes if the API level is less than
 *      1.8.
 * @arg p.itemId The item ID to monitor.
 * @arg p.changeKey If known, the current change key to watch for changes. See @remarks.
 * @arg p.token The Outlook API token.
 * @arg signal The abort signal to listen to instead of the internal signal.
 * @remarks This function has two methods of monitoring, based on the @see p.changeKey value.
 * If the changeKey value is null, the function will monitor the endpoint and watch for an
 * HTTP transition from 404 -> 200 (not ok to ok). This method is used to detect a new item
 * being saved up to the server that does not have a previous draft.
 *
 * If the changeKey value is not null, the function will monitor the ChangeKey data key provided
 * in the response and wait for it to be different than the value provided. This is used to
 * detect changes in an existing saved/draft item. Obtain the changeKey right before calling
 * this function using the value returned from @see getOutlookItemChangeKey.
 *
 * If an abort signal is provided, that is attached and listened to abort the loop. If no signal
 * is provided, a default one that aborts after 30 seconds is provided.
 */
export async function monitorOutlookItemForServerUpdate(
  {
    bodyId,
    itemId,
    changeKey,
    token,
  }: { bodyId: string | null; itemId: string; changeKey: string | null; token: string },
  signal?: AbortSignal,
): Promise<void> {
  const debug = Debug('o365:monitorOutlookItemForServerUpdate');

  const { method, url } = getOutlookURL(itemId, RestEndpoint.GetItem);
  url.searchParams.append('$select', 'ChangeKey');

  // If the current mailbox API is below 1.8, we shouldn't have a way to monitor for the email
  // being updated, so we're going to aim for finding the current Bixel ID in the body. 🤞
  const hasItemIdSupport = isApiSupported('Mailbox', '1.8');
  if (!hasItemIdSupport) {
    if (!bodyId) {
      throw new Error('bodyId is required for API 1.7 and below');
    }

    debug("API doesn't support getItemIdAsync. Will check for this body id to show up", bodyId);
    url.searchParams.append('$select', 'Body');
  }

  const params: RequestInit = {
    ...getOutlookDefaultFetchParams(token),
    method,
    cache: 'no-cache',
    signal,
  };

  // Update the $select parameter to be comma separated, rather than on the URL multiple times.
  // Apparently the Outlook API doesn't like $select=1&$select=2...
  const selectParams = url.searchParams.getAll('$select');
  url.searchParams.set('$select', selectParams.join(','));

  debug('using url and params', { url, params });

  let timeout: ReturnType<typeof setTimeout> | undefined = undefined;
  if (!params.signal) {
    debug('creating internal abort');
    const controller = new AbortController();
    params.signal = controller.signal;
    timeout = setTimeout(() => {
      debug('aborting due to timeout');
      controller.abort();
    }, 30000); // yes - 30 seconds
  }

  let aborted = false;
  const abortHandler = () => {
    debug('aborting. setting escape flag');
    aborted = true;
  };
  params.signal.addEventListener('abort', abortHandler);

  const throwIfAborted = () => {
    if (aborted) {
      debug('throwing since we aborted');
      throw new Error('Check for server update aborted by request.');
    }
  };

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      throwIfAborted();
      const response = await fetch(url.toString(), params);

      // if we don't have a change key and we have an OK response, we're done.
      if (!changeKey && response.ok) {
        if (hasItemIdSupport) {
          debug('check. response ok for new mail appearing on server');
          return;
        }

        const message = (await response.json()) as Partial<OutlookRestMessage>;
        if (bodyId && (message?.Body?.Content?.indexOf(bodyId) ?? -1) > -1) {
          debug('check. response ok for old API. body id found');
          return;
        }

        debug('check. legacy api, body different');
      }

      // if we do have a change key, a bit more checking is involved.
      if (changeKey) {
        if (response.ok) {
          const message = (await response.json()) as Partial<OutlookRestMessage>;
          if (message?.ChangeKey && message.ChangeKey !== changeKey) {
            debug('check. key changed', { old: changeKey, new: message.ChangeKey });
            return;
          }

          debug('check. key identical');
        } else {
          debug('check. response not ok');
          throw new RestError('Got back unexpected result from existing draft', response);
        }
      }

      throwIfAborted();
      await wait(500);
    }
  } finally {
    params.signal.removeEventListener('abort', abortHandler);
    clearTimeout(timeout as ReturnType<typeof setTimeout>);
  }
}
